import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/roles";
import { ArrowLeft, CheckCircle2, AlertTriangle, X, ClipboardCheck, Play, FileDown, Sparkles, ListOrdered, Archive, MessageSquare, Send } from "lucide-react";
import generateDisplayCheckPdf from "@/lib/displayPdf";
import SortableZoneList from "@/components/SortableZoneList";
import AuthImage, { photoSrcCandidate } from "@/components/AuthImage";
import { formatPhotoCaption } from "@/lib/photoCaption";

const CONDITION_LABEL = { excellent: "Excellent", good: "Good", needs_attention: "Needs Attention", critical: "Critical" };
const CONDITION_CLASS = {
  excellent: "text-emerald-400", good: "text-[color:var(--gold)]",
  needs_attention: "text-[color:var(--warning)]", critical: "text-[color:var(--error)]",
};
const CLEAN_LABEL = { very_clean: "Very Clean", clean: "Clean", needs_cleaning: "Needs Cleaning", dust_present: "Dust Present", dirty: "Dirty", critical: "Critical Cleaning" };
const DUST_LABEL = { none: "No Visible Dust", light: "Light Dust", moderate: "Moderate Dust", heavy: "Heavy Dust" };
const cleanLabel = (k) => CLEAN_LABEL[k] || "—";
const dustLabel = (k) => DUST_LABEL[k] || "—";

export default function DisplayDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [dc, setDc] = useState(null);
  const [selected, setSelected] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [approving, setApproving] = useState(false);
  const [correctingZone, setCorrectingZone] = useState(null);
  const [correctionReason, setCorrectionReason] = useState("");
  const [reordering, setReordering] = useState(false);
  const [orderDraft, setOrderDraft] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);

  const canReview = hasAccess(user?.role, ["admin", "gm"]);
  const canGlobal = hasAccess(user?.role, ["admin", "global_standards"]);
  const isMyCheck = dc && dc.manager_id === user?.id;
  const canReorder = dc && (hasAccess(user?.role, ["admin", "gm"]) || isMyCheck)
    && !["submitted", "completed", "resubmitted_to_gm", "awaiting_global_review", "returned_to_gm"].includes(dc.status)
    && (dc.zones || []).length > 1;
  const canArchive = dc && hasAccess(user?.role, ["admin", "gm"]) && dc.status === "completed" && !dc.is_archived;
  const canUnarchive = dc && hasAccess(user?.role, ["admin", "gm"]) && dc.is_archived;

  const gmSendBack = async () => {
    const reason = window.prompt("Instructions for the manager:\n(what needs to be corrected?)");
    if (!reason || !reason.trim()) return;
    try { await api.post(`/display_checks/${id}/gm_send_back`, { reason }); toast.success("Sent back to manager"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const gmApproveToGlobal = async () => {
    try { await api.post(`/display_checks/${id}/gm_approve`, { comments: "" }); toast.success("Sent to Global Standards"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const globalFinal = async () => {
    if (!window.confirm("Give FINAL APPROVAL to this Display Check? The weighted scores will lock.")) return;
    try { await api.post(`/display_checks/${id}/global_final_approve`, { comments: "" }); toast.success("Final approved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const globalReturn = async () => {
    const reason = window.prompt("Comments for the GM:");
    if (!reason || !reason.trim()) return;
    try { await api.post(`/display_checks/${id}/global_return_to_gm`, { reason }); toast.success("Returned to GM"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const gmReassign = async () => {
    const reason = window.prompt("Additional instructions for the manager (Global Standards notes will be attached):");
    if (reason === null) return;
    try { await api.post(`/display_checks/${id}/gm_reassign_to_manager`, { reason: reason || "Please correct per Global Standards notes." }); toast.success("Reassigned to manager"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const managerResubmit = async () => {
    try { await api.post(`/display_checks/${id}/manager_resubmit`, {}); toast.success("Resubmitted to GM"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const archive = async () => {
    const reason = window.prompt("Reason for removing this Display Check from the active list?\n(It will remain in Reports; leave blank to skip.)");
    if (reason === null) return;
    try {
      await api.post(`/display_checks/${id}/archive`, { reason });
      toast.success("Display Check archived — still visible in Reports");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unarchive = async () => {
    if (!window.confirm("Restore this Display Check to the active list?")) return;
    try { await api.post(`/display_checks/${id}/unarchive`, {}); toast.success("Restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/display_checks/${id}`);
      setDc(data);
      if (!selected && (data.zones || []).length > 0) setSelected(data.zones[0].zone_id);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  if (!dc) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;

  const currentZone = (dc.zones || []).find((z) => z.zone_id === selected) || dc.zones?.[0];
  const openPhoto = (photo, zoneId, issueKey) => {
    setPreviewPhoto({
      photo,
      zone_id: zoneId || currentZone?.zone_id,
      photo_kind: issueKey ? "issue" : "zone",
      issue_key: issueKey || null,
    });
  };
  const canComment = hasAccess(user?.role, ["admin", "gm", "global_standards"]);

  const requestCorrection = async () => {
    if (!correctionReason.trim()) return toast.error("Please describe the correction needed");
    try {
      await api.post(`/display_checks/${id}/request_correction`, {
        zone_id: correctingZone, reason: correctionReason.trim(),
      });
      toast.success("Correction requested — manager notified");
      setCorrectingZone(null); setCorrectionReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="dc-detail">
      <button onClick={() => nav(-1)} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back</button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Display Check</div>
          <h1 className="font-heading text-3xl">{dc.check_number}</h1>
          <div className="text-sm text-[color:var(--text-secondary)] mt-1">{dc.store_name} · {dc.inspection_date}</div>
          <div className="text-sm">Manager: <span className="text-[color:var(--gold)]">{dc.manager_name}</span></div>
          {dc.reviewed_by_name && <div className="text-xs text-[color:var(--text-secondary)]">Reviewed by {dc.reviewed_by_name}</div>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs px-3 py-1.5 rounded-full border border-[color:var(--gold)]/40 text-[color:var(--gold)] uppercase" data-testid="dc-status">{dc.status.replace(/_/g, " ")}</span>
          {isMyCheck && ["assigned", "in_progress", "correction_required"].includes(dc.status) && (
            <Link to={`/display/${id}/run`} className="hos-btn-primary flex items-center gap-2" data-testid="dc-continue-btn">
              <Play size={14}/> {dc.status === "assigned" ? "Start" : "Continue"}
            </Link>
          )}
          {canReview && ["submitted", "resubmitted_to_gm", "returned_to_gm"].includes(dc.status) && (
            <>
              {dc.status === "returned_to_gm" ? (
                <button className="hos-btn-primary flex items-center gap-2" onClick={gmReassign} data-testid="dc-reassign-btn">
                  <ArrowLeft size={14}/> Reassign to Manager
                </button>
              ) : (
                <>
                  <button className="hos-btn-primary flex items-center gap-2" onClick={gmApproveToGlobal} data-testid="dc-gm-approve-btn">
                    <CheckCircle2 size={14}/> Approve → Global Standards
                  </button>
                  <button className="hos-btn-outline flex items-center gap-2 text-[color:var(--warning)] border-[color:var(--warning)]/40" onClick={gmSendBack} data-testid="dc-send-back-btn">
                    <AlertTriangle size={14}/> Send Back to Manager
                  </button>
                </>
              )}
            </>
          )}
          {canGlobal && dc.status === "awaiting_global_review" && (
            <>
              <button className="hos-btn-primary flex items-center gap-2" onClick={globalFinal} data-testid="dc-global-approve-btn">
                <CheckCircle2 size={14}/> Final Approve
              </button>
              <button className="hos-btn-outline flex items-center gap-2 text-[color:var(--warning)] border-[color:var(--warning)]/40" onClick={globalReturn} data-testid="dc-global-return-btn">
                <ArrowLeft size={14}/> Return to GM
              </button>
            </>
          )}
          {isMyCheck && dc.status === "manager_correction_required" && (
            <button className="hos-btn-primary flex items-center gap-2" onClick={managerResubmit} data-testid="dc-resubmit-btn">
              <CheckCircle2 size={14}/> Resubmit to GM
            </button>
          )}
          {canReorder && (
            <button className="hos-btn-outline flex items-center gap-2" onClick={() => { setOrderDraft((dc.zones || []).map((z) => z.zone_id)); setReordering(true); }} data-testid="dc-reorder-btn">
              <ListOrdered size={14}/> Reorder Zones
            </button>
          )}
          <button className="hos-btn-outline flex items-center gap-2" onClick={() => generateDisplayCheckPdf(dc)} data-testid="dc-pdf-btn">
            <FileDown size={14}/> Export PDF
          </button>
          {canArchive && (
            <button className="py-2 px-3 rounded-md border border-[color:var(--warning)]/50 text-[color:var(--warning)] hover:bg-[color:var(--warning)]/10 flex items-center gap-2 text-sm" onClick={archive} data-testid="dc-archive-btn">
              <Archive size={14}/> Remove from active list
            </button>
          )}
          {canUnarchive && (
            <button className="hos-btn-outline flex items-center gap-2" onClick={unarchive} data-testid="dc-unarchive-btn">
              <Archive size={14}/> Restore
            </button>
          )}
        </div>
      </div>

      {reordering && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !savingOrder && setReordering(false)}>
          <div className="hos-card p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dc-reorder-modal">
            <div className="font-heading text-2xl mb-1">Zone Walking Order</div>
            <div className="text-sm text-[color:var(--text-secondary)] mb-4">Drag zones into the exact order the manager should inspect them.</div>
            <SortableZoneList
              testIdPrefix="dc-order"
              items={orderDraft
                .map((zid) => (dc.zones || []).find((z) => z.zone_id === zid))
                .filter(Boolean)
                .map((z) => ({ id: z.zone_id, label: z.zone_name, subtitle: z.floor ? `Floor: ${z.floor}` : undefined }))}
              onChange={setOrderDraft}
            />
            <div className="flex justify-end gap-3 mt-5">
              <button className="hos-btn-outline" onClick={() => setReordering(false)} disabled={savingOrder}>Cancel</button>
              <button
                className="hos-btn-primary"
                disabled={savingOrder}
                onClick={async () => {
                  setSavingOrder(true);
                  try {
                    await api.put(`/display_checks/${id}/reorder_zones`, { zone_ids: orderDraft });
                    toast.success("Zone order updated");
                    setReordering(false);
                    load();
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setSavingOrder(false); }
                }}
                data-testid="dc-reorder-save"
              >
                {savingOrder ? "Saving…" : "Save Order"}
              </button>
            </div>
          </div>
        </div>
      )}

      {dc.correction_instructions && ["manager_correction_required", "correction_required"].includes(dc.status) && (
        <div className="hos-card p-4 border border-[color:var(--warning)]/40">
          <div className="label-caps text-[color:var(--warning)] mb-1">Correction Required</div>
          <div className="text-sm whitespace-pre-wrap">{dc.correction_instructions}</div>
        </div>
      )}
      {dc.global_return_reason && dc.status === "returned_to_gm" && (
        <div className="hos-card p-4 border border-[color:var(--warning)]/40">
          <div className="label-caps text-[color:var(--warning)] mb-1">Returned by Global Standards</div>
          <div className="text-sm whitespace-pre-wrap">{dc.global_return_reason}</div>
          <div className="text-xs text-[color:var(--text-secondary)] mt-2">by {dc.global_returned_by_name}</div>
        </div>
      )}

      {(dc.history || []).length > 0 && (
        <details className="hos-card p-4" data-testid="dc-timeline">
          <summary className="cursor-pointer label-caps">Approval Timeline ({dc.history.length} events)</summary>
          <ol className="mt-3 space-y-2 text-sm">
            {dc.history.map((h, i) => (
              <li key={i} className="flex items-start gap-3 border-l-2 border-[color:var(--gold)]/40 pl-3">
                <div className="flex-1">
                  <div className="font-semibold">{(h.action || "").replace(/_/g, " ")}</div>
                  <div className="text-xs text-[color:var(--text-secondary)]">
                    {h.user_name} ({h.role}) · {new Date(h.at).toLocaleString()}
                    {h.reason ? ` · ${h.reason}` : ""}
                    {h.comments ? ` · ${h.comments}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </details>
      )}

      {dc.instructions && (
        <div className="hos-card p-3 text-sm">
          <div className="label-caps mb-1">Instructions</div>{dc.instructions}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
        <div className="hos-card p-2 space-y-1 max-h-[600px] overflow-y-auto">
          {(dc.zones || []).map((z, i) => {
            const active = z.zone_id === currentZone?.zone_id;
            const done = ["completed", "corrected"].includes(z.status);
            const needsFix = z.status === "correction_required";
            return (
              <button key={z.zone_id} onClick={() => setSelected(z.zone_id)}
                      className={`w-full text-left p-2 rounded-md text-sm ${active ? "bg-[color:var(--gold)]/15 border border-[color:var(--gold)]" : "border border-transparent hover:bg-[color:var(--bg-surface-2)]"}`}
                      data-testid={`zone-tab-${z.zone_id}`}>
                <div className="flex items-center justify-between">
                  <span className="truncate">{i + 1}. {z.zone_name}</span>
                  {done && <CheckCircle2 size={14} className="text-emerald-400"/>}
                  {needsFix && <AlertTriangle size={14} className="text-[color:var(--warning)]"/>}
                </div>
                {z.condition && <div className={`text-[10px] ${CONDITION_CLASS[z.condition]}`}>{CONDITION_LABEL[z.condition]}</div>}
              </button>
            );
          })}
        </div>

        <div>
          {currentZone && (
            <ZoneCard
              zone={currentZone}
              onOpenPhoto={openPhoto}
              canReview={canReview}
              dcStatus={dc.status}
              onRequestCorrection={() => { setCorrectingZone(currentZone.zone_id); setCorrectionReason(""); }}
            />
          )}
        </div>
      </div>

      {previewPhoto && (
        <PhotoPreviewModal
          entry={previewPhoto}
          checkId={id}
          canComment={canComment}
          onClose={() => setPreviewPhoto(null)}
          onCommentAdded={() => load()}
        />
      )}

      {correctingZone && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setCorrectingZone(null)}>
          <div className="hos-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="correction-modal">
            <div className="font-heading text-2xl mb-3">Request Correction</div>
            <div className="text-sm text-[color:var(--text-secondary)] mb-3">The manager will resubmit this zone with new photos.</div>
            <textarea className="hos-input min-h-[100px]" placeholder="Describe what needs to be corrected…" value={correctionReason} onChange={(e) => setCorrectionReason(e.target.value)} data-testid="correction-reason"/>
            <div className="flex justify-end gap-3 mt-4">
              <button className="hos-btn-outline" onClick={() => setCorrectingZone(null)}>Cancel</button>
              <button className="hos-btn-primary" onClick={requestCorrection} data-testid="correction-send">Send</button>
            </div>
          </div>
        </div>
      )}

      {(dc.history || []).length > 0 && (
        <details className="hos-card p-4">
          <summary className="cursor-pointer font-heading text-lg">Audit Trail ({dc.history.length})</summary>
          <div className="mt-3 space-y-2 text-xs">
            {dc.history.map((h, i) => (
              <div key={i} className="border-l-2 border-[color:var(--gold)]/40 pl-3">
                <div><span className="text-[color:var(--gold)]">{h.action}</span> · {h.user_name}</div>
                <div className="text-[color:var(--text-secondary)]">{new Date(h.timestamp).toLocaleString()}</div>
                {h.details && Object.keys(h.details).length > 0 && (
                  <div className="text-[color:var(--text-secondary)]">{JSON.stringify(h.details)}</div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function ZoneCard({ zone, onOpenPhoto, canReview, dcStatus, onRequestCorrection }) {
  const originals = (zone.photos || []).filter((p) => (p.revision || 0) === 0);
  const corrections = (zone.photos || []).filter((p) => (p.revision || 0) >= 1);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiVerdict, setAiVerdict] = useState(zone.ai_compare || null);
  const params = useParams();
  const canCompare = originals.length > 0 && corrections.length > 0;

  const runCompare = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/display_checks/${params.id}/zones/${zone.zone_id}/ai_compare`, {});
      setAiVerdict(data);
      toast.success(data.fixed === true ? "AI: display appears fixed" : data.fixed === false ? "AI: fix not detected" : "AI inconclusive");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="hos-card p-4 space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="font-heading text-2xl">{zone.zone_name}</div>
          <div className="text-xs text-[color:var(--text-secondary)]">{zone.floor && `Floor: ${zone.floor}`}</div>
        </div>
        {zone.condition && (
          <span className={`text-sm px-3 py-1 rounded-full border border-current ${CONDITION_CLASS[zone.condition]}`}>{CONDITION_LABEL[zone.condition]}</span>
        )}
      </div>

      {zone.correction_reason && zone.status === "correction_required" && (
        <div className="p-3 border border-[color:var(--warning)] bg-[color:var(--warning)]/10 rounded-md text-sm">
          <div className="flex items-center gap-2 text-[color:var(--warning)] font-semibold mb-1">
            <AlertTriangle size={14}/> Correction Requested
          </div>
          {zone.correction_reason}
        </div>
      )}

      {zone.status === "pending" ? (
        <div className="text-[color:var(--text-secondary)] text-sm">Zone not yet inspected.</div>
      ) : (
        <>
          {(zone.zone_owner_names || []).length > 0 && (
            <div>
              <div className="label-caps">Zone Owner(s) — For this inspection</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {zone.zone_owner_names.map((n, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-full bg-[color:var(--gold)]/15 border border-[color:var(--gold)]/30 text-[color:var(--gold)]">{n}</span>
                ))}
              </div>
              <div className="text-[10px] text-[color:var(--text-secondary)] mt-1">Zone Owners are selected per inspection — this list is historical and won't change with future checks.</div>
            </div>
          )}

          {zone.fandom_names?.length > 0 && (
            <div>
              <div className="label-caps">Fandom(s)</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {zone.fandom_names.map((n, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded bg-[color:var(--gold)]/10 text-[color:var(--gold)]">{n}</span>
                ))}
              </div>
            </div>
          )}

          {(zone.standards_snapshot || []).length > 0 && (
            <div className="p-3 rounded-md border border-[color:var(--gold)]/30 bg-[color:var(--gold)]/5">
              <div className="flex items-center justify-between mb-2">
                <div className="label-caps">Display Score</div>
                <div className="text-[color:var(--gold)] font-heading text-2xl">
                  {(zone.final_display_score ?? zone.display_score) === null ? "—" : Math.round(zone.final_display_score ?? zone.display_score)}
                  <span className="text-[10px]">/100</span>
                </div>
              </div>
              <table className="w-full text-xs">
                <thead className="text-left"><tr className="label-caps"><th className="p-1">Standard</th><th className="p-1 text-right">Score</th><th className="p-1 text-right">Weight</th><th className="p-1 text-right">Contribution</th></tr></thead>
                <tbody>
                  {zone.standards_snapshot.map((it) => (
                    <tr key={it.standard_id} className="border-t border-[color:var(--border)]/50">
                      <td className="p-1">{it.name_snapshot}</td>
                      <td className="p-1 text-right">{it.score_percentage === null ? "—" : `${Math.round(it.score_percentage)}%`}</td>
                      <td className="p-1 text-right">{Math.round(it.weight_snapshot)}%</td>
                      <td className="p-1 text-right text-[color:var(--gold)]">{it.weighted_contribution === null ? "—" : it.weighted_contribution.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {zone.standards_valid === false && <div className="text-[10px] text-[color:var(--warning)] mt-1">⚠ Weights invalid at inspection time — score not published.</div>}
            </div>
          )}

          {(zone.cleanliness_status || zone.dust_condition || Object.keys(zone.cleanliness_checklist || {}).length > 0) && (
            <div className="p-3 rounded-md border border-[color:var(--border)] bg-[color:var(--bg-surface-2)]/40">
              <div className="label-caps mb-2">Cleanliness</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-[color:var(--text-secondary)]">Overall:</span> <span className="font-semibold">{cleanLabel(zone.cleanliness_status)}</span></div>
                <div><span className="text-[color:var(--text-secondary)]">Dust:</span> <span className="font-semibold">{dustLabel(zone.dust_condition)}</span></div>
                {typeof zone.cleanliness_score === "number" && (
                  <div className="col-span-2"><span className="text-[color:var(--text-secondary)]">Score:</span> <span className="text-[color:var(--gold)] font-semibold">{zone.cleanliness_score}%</span></div>
                )}
              </div>
              {Object.keys(zone.cleanliness_checklist || {}).length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-2 text-xs">
                  {Object.entries(zone.cleanliness_checklist).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between p-1.5 rounded bg-[color:var(--bg-surface-2)]">
                      <span className="capitalize">{k.replace(/_/g, " ")}</span>
                      <span className={v === true ? "text-emerald-400" : v === false ? "text-[color:var(--error)]" : "text-[color:var(--text-secondary)]"}>
                        {v === true ? "Yes" : v === false ? "No" : "N/A"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {zone.cleanliness_notes && (
                <div className="mt-2 text-xs"><span className="text-[color:var(--text-secondary)]">Notes:</span> {zone.cleanliness_notes}</div>
              )}
            </div>
          )}

          <div>
            <div className="label-caps">Stock &amp; Merchandising Checklist</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-1 text-sm">
              {Object.entries(zone.checklist || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between p-2 rounded bg-[color:var(--bg-surface-2)]">
                  <span className="capitalize">{k.replace(/_/g, " ")}</span>
                  <span className={v ? "text-emerald-400" : "text-[color:var(--error)]"}>{v ? "Yes" : "No"}</span>
                </div>
              ))}
            </div>
          </div>

          {(zone.issues || []).length > 0 && (
            <div>
              <div className="label-caps">Issues</div>
              <div className="space-y-2 mt-1">
                {zone.issues.map((k) => {
                  const ip = (zone.issue_photos || {})[k] || [];
                  return (
                    <div key={k} className="p-2 rounded border border-[color:var(--warning)]/30 bg-[color:var(--warning)]/5">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[color:var(--warning)] capitalize">{k.replace(/_/g, " ")}</span>
                        <span className="text-[10px] text-[color:var(--text-secondary)]">{ip.length} photo(s)</span>
                      </div>
                      {ip.length > 0 && <div className="mt-2"><PhotoGrid photos={ip} onOpen={(p) => onOpenPhoto(p, k)} issueKey={k}/></div>}
                    </div>
                  );
                })}
              </div>
              {zone.other_issue_detail && <div className="text-xs text-[color:var(--text-secondary)] mt-1">Other: {zone.other_issue_detail}</div>}
            </div>
          )}

          {zone.comments && (
            <div><div className="label-caps">Comments</div><div className="text-sm">{zone.comments}</div></div>
          )}
          {zone.action_taken && (
            <div><div className="label-caps">Action Taken</div><div className="text-sm">{zone.action_taken}</div></div>
          )}

          {originals.length > 0 && (
            <div>
              <div className="label-caps mb-1">Main Zone Photos ({corrections.length > 0 ? "Before" : originals.length})</div>
              <PhotosByArea photos={originals} onOpen={(p) => onOpenPhoto(p)}/>
            </div>
          )}
          {corrections.length > 0 && (
            <div>
              <div className="label-caps mb-1 text-emerald-400">After Correction ({corrections.length})</div>
              <PhotoGrid photos={corrections} onOpen={(p) => onOpenPhoto(p)}/>
            </div>
          )}

          {canCompare && (
            <div className="p-3 rounded-md border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <Sparkles size={14} className="text-[color:var(--gold)]"/>
                  <span className="font-semibold">AI Photo Compare</span>
                  <span className="text-xs text-[color:var(--text-secondary)]">Powered by Gemini 2.5 Flash</span>
                </div>
                <button className="hos-btn-outline text-xs py-1 px-3" onClick={runCompare} disabled={aiBusy} data-testid={`ai-compare-${zone.zone_id}`}>
                  {aiBusy ? "Analysing…" : aiVerdict ? "Re-analyse" : "Analyse Before/After"}
                </button>
              </div>
              {aiVerdict && (
                <div className="mt-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${aiVerdict.fixed === true ? "bg-emerald-500/20 text-emerald-300" : aiVerdict.fixed === false ? "bg-red-500/20 text-red-300" : "bg-[color:var(--warning)]/20 text-[color:var(--warning)]"}`}>
                      {aiVerdict.fixed === true ? "FIXED" : aiVerdict.fixed === false ? "NOT FIXED" : "INCONCLUSIVE"}
                    </span>
                    <span className="text-xs text-[color:var(--text-secondary)]">confidence {(Number(aiVerdict.confidence || 0) * 100).toFixed(0)}%</span>
                  </div>
                  {aiVerdict.notes && <div className="text-xs text-[color:var(--text-secondary)] mt-2">{aiVerdict.notes}</div>}
                  {(aiVerdict.findings || []).length > 0 && (
                    <ul className="text-xs mt-2 list-disc pl-4 text-[color:var(--text-secondary)]">
                      {aiVerdict.findings.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {canReview && dcStatus === "submitted" && (
            <button className="hos-btn-outline flex items-center gap-2" onClick={onRequestCorrection} data-testid={`request-correction-${zone.zone_id}`}>
              <AlertTriangle size={14}/> Request Correction on this Zone
            </button>
          )}
        </>
      )}
    </div>
  );
}

function PhotoGrid({ photos, onOpen, issueKey }) {
  return (
    <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
      {photos.map((p) => {
        const cap = formatPhotoCaption(p);
        const nComments = (p.comments || []).length;
        return (
          <div key={p.id}>
            <button
              onClick={() => onOpen(p, issueKey)}
              className="w-full aspect-square rounded overflow-hidden border border-[color:var(--border)] hover:border-[color:var(--gold)] block relative"
              data-testid={`photo-thumb-${p.id}`}
            >
              <AuthImage photo={p} className="w-full h-full object-cover"/>
              {nComments > 0 && (
                <span
                  className="absolute top-1 right-1 flex items-center gap-0.5 text-[10px] bg-[color:var(--gold)] text-black rounded-full px-1.5 py-0.5 font-bold shadow"
                  data-testid={`photo-comment-count-${p.id}`}
                >
                  <MessageSquare size={9}/> {nComments}
                </span>
              )}
            </button>
            {(cap.primary || cap.secondary) && (
              <div className="mt-1 text-[10px] leading-tight" data-testid={`detail-photo-caption-${p.id}`}>
                <div className="text-[color:var(--gold)] font-semibold uppercase truncate">{cap.primary}</div>
                <div className="text-[color:var(--text-secondary)] truncate">{cap.secondary}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PhotosByArea({ photos, onOpen, issueKey }) {
  const groups = photos.reduce((m, p) => {
    const key = p.display_area_id || "_unassigned";
    if (!m[key]) m[key] = { code: p.display_area_code || "", name: p.display_area_name || "", photos: [] };
    m[key].photos.push(p);
    return m;
  }, {});
  const entries = Object.values(groups);
  if (entries.length <= 1 && !entries[0]?.code) return <PhotoGrid photos={photos} onOpen={onOpen} issueKey={issueKey}/>;
  return (
    <div className="space-y-3">
      {entries.map((g, i) => (
        <div key={i}>
          <div className="text-xs font-semibold text-[color:var(--gold)] mb-1">
            {g.code ? `${g.code} — ${g.name}` : "Unassigned Area"} <span className="text-[color:var(--text-secondary)] font-normal">({g.photos.length})</span>
          </div>
          <PhotoGrid photos={g.photos} onOpen={onOpen} issueKey={issueKey}/>
        </div>
      ))}
    </div>
  );
}

function PhotoPreviewModal({ entry, checkId, canComment, onClose, onCommentAdded }) {
  const { photo, zone_id, photo_kind, issue_key } = entry;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(photo);
  const cap = formatPhotoCaption(live);
  const comments = live.comments || [];

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/display_checks/${checkId}/photo_comment`, {
        zone_id,
        photo_id: live.id,
        text: t,
        photo_kind: photo_kind || "zone",
        issue_key: issue_key || undefined,
      });
      setLive({ ...live, comments: [...comments, data.comment] });
      setText("");
      toast.success("Comment posted");
      onCommentAdded && onCommentAdded();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="photo-preview-modal">
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_360px] gap-4 max-w-6xl w-full my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative flex items-center justify-center">
          <AuthImage photo={live} alt="" className="max-h-[85vh] max-w-full rounded"/>
          <button className="absolute top-2 right-2 text-white bg-black/50 rounded-full p-1" onClick={onClose}><X size={22}/></button>
        </div>
        <div className="hos-card p-4 max-h-[85vh] overflow-y-auto flex flex-col">
          <div className="label-caps">Photo Detail</div>
          <div className="text-[color:var(--gold)] font-semibold uppercase mt-1">{cap.primary || "—"}</div>
          <div className="text-xs text-[color:var(--text-secondary)]">{cap.secondary}</div>
          <div className="mt-4 pt-4 border-t border-[color:var(--border)] flex-1">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={14} className="text-[color:var(--gold)]"/>
              <span className="label-caps">Review Comments ({comments.length})</span>
            </div>
            {comments.length === 0 && (
              <div className="text-xs text-[color:var(--text-secondary)]">No comments yet.</div>
            )}
            <ul className="space-y-2">
              {comments.map((c) => (
                <li key={c.id} className="p-2 rounded bg-[color:var(--bg-surface-2)]/60 border border-[color:var(--border)]" data-testid={`comment-${c.id}`}>
                  <div className="text-sm whitespace-pre-wrap">{c.text}</div>
                  <div className="text-[10px] text-[color:var(--text-secondary)] mt-1 flex items-center gap-1">
                    <span className="font-semibold">{c.author_name}</span>
                    <span className="uppercase px-1 rounded bg-[color:var(--gold)]/20 text-[color:var(--gold)]">{(c.author_role || "").replace(/_/g, " ")}</span>
                    <span>· {new Date(c.at).toLocaleString()}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {canComment ? (
            <div className="mt-4 pt-4 border-t border-[color:var(--border)]">
              <label className="label-caps">Add Comment</label>
              <textarea
                className="hos-input min-h-[80px] mt-1"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Note what's good, what needs fixing, or clarify for the GM / manager…"
                data-testid="photo-comment-input"
              />
              <button
                className="hos-btn-primary mt-2 w-full flex items-center justify-center gap-2"
                onClick={submit}
                disabled={busy || !text.trim()}
                data-testid="photo-comment-submit"
              >
                <Send size={14}/> {busy ? "Posting…" : "Post Comment"}
              </button>
              <div className="text-[10px] text-[color:var(--text-secondary)] mt-2">
                Comments appear here for GM and Global Standards review, and are printed in the exported PDF.
              </div>
            </div>
          ) : (
            <div className="mt-4 pt-4 border-t border-[color:var(--border)] text-[11px] text-[color:var(--text-secondary)]">
              Comments can be added by Manager, GM and Global Standards reviewers.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
